import { type ButtonHTMLAttributes, forwardRef, memo, useMemo } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
}

const Button = memo(forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...props
}, ref) => {
  // Memoized base classes with better accessibility and responsive design
  const baseClasses = useMemo(() => `
    inline-flex items-center justify-center font-medium rounded-lg 
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50
    disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
    active:scale-95 transform
    high-contrast:border high-contrast:border-gray-600
    min-h-[44px] sm:min-h-[40px]
  `.replace(/\s+/g, ' ').trim(), [])

  // Memoized variant classes with better contrast and accessibility
  const variantClasses = useMemo(() => ({
    primary: `
      bg-[#5761EB] text-white hover:bg-indigo-700 active:bg-indigo-800
      focus:ring-indigo-500 shadow-sm hover:shadow-md
      high-contrast:bg-blue-700 high-contrast:border-blue-800
    `.replace(/\s+/g, ' ').trim(),
    secondary: `
      bg-gray-100 text-gray-900 hover:bg-gray-200 active:bg-gray-300
      focus:ring-gray-500 border border-gray-200 hover:border-gray-300
      high-contrast:bg-gray-200 high-contrast:border-gray-400
    `.replace(/\s+/g, ' ').trim(),
    outline: `
      border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100
      focus:ring-indigo-500 hover:border-gray-400
      high-contrast:border-gray-600 high-contrast:text-gray-900
    `.replace(/\s+/g, ' ').trim(),
    ghost: `
      text-gray-700 hover:bg-gray-100 active:bg-gray-200
      focus:ring-gray-500 hover:text-gray-900
      high-contrast:text-gray-900 high-contrast:hover:bg-gray-200
    `.replace(/\s+/g, ' ').trim(),
    danger: `
      bg-red-600 text-white hover:bg-red-700 active:bg-red-800
      focus:ring-red-500 shadow-sm hover:shadow-md
      high-contrast:bg-red-700 high-contrast:border-red-800
    `.replace(/\s+/g, ' ').trim(),
    success: `
      bg-[#24C661] text-white hover:bg-[#18a64e] active:bg-[#18a64e]
      focus:ring-[#18a64e] shadow-sm hover:shadow-md
      high-contrast:bg-[#18a64e] high-contrast:border-[#18a64e]
    `.replace(/\s+/g, ' ').trim(),
  }), [])

  // Memoized responsive size classes
  const sizeClasses = useMemo(() => ({
    sm: 'px-3 py-2 text-sm min-w-[80px] sm:min-w-[64px]',
    md: 'px-4 py-3 text-sm min-w-[100px] sm:min-w-[80px]',
    lg: 'px-6 py-3 text-base min-w-[120px] sm:min-w-[100px]'
  }), [])

  // Memoized combined classes
  const buttonClasses = useMemo(() => {
    const widthClasses = fullWidth ? 'w-full' : ''
    return `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClasses} ${className}`
  }, [baseClasses, variantClasses, variant, sizeClasses, size, fullWidth, className])

  // Memoized loading spinner
  const LoadingSpinner = useMemo(() => (
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
  ), [])

  return (
    <button
      ref={ref}
      className={buttonClasses}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && LoadingSpinner}
      {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  )
}))

Button.displayName = 'Button'

export default Button
export { Button }